import { DatePicker } from '@/entities/datePicker/ui/DatePicker'
import { TopupStatus } from '@/entities/topups'
import CardIcon from '@/shared/assets/icons/card.svg?react'
import CrossIcon from '@/shared/assets/icons/cross.svg?react'
import FilterIcon from '@/shared/assets/icons/filter.svg?react'
import { BaseButton, BaseInput } from '@/shared/components'
import { useToast } from '@/shared/hooks'
import { OptionModel } from '@/shared/lib/types'
import { useEffect, useState } from 'react'
import { ControlledSelect } from '../ControledFilterComponents/ControlledSelect'

export type StateType = {
  transactionId: string
  paymentRequisit: string
  amountFrom: string
  amountTo: string
  status: TopupStatus | undefined
  bank: string
  method: string
  startDate: string
  endDate: string
}

const statusOptions: OptionModel[] = [
  { label: 'Все', value: '', index: 0 },
  { label: 'Ожидает оплаты', value: TopupStatus.WaitingPayment, index: 1 },
  { label: 'Подтвержден плательщиком', value: TopupStatus.ConfirmedByPayer, index: 2 },
  { label: 'Платеж выполнен', value: TopupStatus.Paid, index: 3 },
  { label: 'Платеж отменен', value: TopupStatus.Cancelled, index: 4 },
  { label: 'Время ожидания истекло', value: TopupStatus.Timeout, index: 5 },
  { label: 'Переплата', value: TopupStatus.OverPaid, index: 6 },
  { label: 'Частичная оплата', value: TopupStatus.PartiallyPaid, index: 7 },
  { label: 'Платеж возвращен', value: TopupStatus.Returned, index: 8 },
  { label: 'Спор', value: TopupStatus.Disput, index: 9 },
]
const bankOptions: OptionModel[] = [{ label: 'Все', value: 'all', index: 0 }]
const paymentMethosOptions: OptionModel[] = [{ label: 'Все', value: 'all', index: 0 }]

type FiltersBlockProps = {
  onSubmit: (filters: StateType) => void
  onReset?: () => void
}

export const FiltersBlock = ({ onSubmit, onReset }: FiltersBlockProps) => {
  const initialFilters: StateType = {
    transactionId: '',
    paymentRequisit: '',
    amountFrom: '',
    amountTo: '',
    status: undefined,
    bank: 'all',
    method: 'all',
    startDate: '',
    endDate: '',
  }
  const [isOpen, setIsOpen] = useState(false)
  const [filters, setFilters] = useState<StateType>(initialFilters)

  const { addToast } = useToast()
  const [error, setError] = useState('')

  useEffect(() => {
    if (error) {
      addToast('Ошибка!', error, 'error')
    }
  }, [error, setError])

  const handleFilterChange = (key: keyof StateType, value: string | number | null | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value ?? '' }))
  }

  const icon = isOpen ? (
    <CrossIcon className="text-primary dark:text-primary-dark" />
  ) : (
    <FilterIcon className="text-primary dark:text-primary-dark" />
  )

  const handleReset = () => {
    setFilters(initialFilters)
    onReset?.()
    console.log('click')
  }

  return (
    <div className="mb-6">
      <div className="flex md:flex-row flex-col-reverse md:items-center gap-4 mb-6">
        <BaseButton
          onClick={() => {
            setIsOpen((prev) => {
              const next = !prev
              if (!next && onReset) onReset()
              return next
            })
          }}
          radius="lg"
          variant="setting"
          icon={icon}
          padding="empty"
          className="px-4 py-2 min-w-[130px]"
        >
          {isOpen ? 'Закрыть' : 'Фильтры'}
        </BaseButton>
        <div className="flex items-center gap-x-4 w-full">
          <BaseInput
            className="w-full"
            placeholder="Введите ID для поиска транзакции"
            value={filters.transactionId}
            onChange={(e) => handleFilterChange('transactionId', e.target.value)}
          />
          <BaseButton radius="lg" onClick={() => onSubmit(filters)}>
            Найти
          </BaseButton>
        </div>
      </div>
      {isOpen && (
        <div className="flex flex-col gap-y-6 bg-light-purple dark:bg-dark-purple px-6 py-8 rounded-2xl text-sm">
          <p className="font-medium text-base">Фильтрация транзакций</p>
          <div className="flex md:flex-row flex-col flex-wrap gap-6 md:gap-y-4">
            <div className="flex-1">
              <span className="inline-block mb-2 text-text-secondary whitespace-nowrap">
                Реквизиты получателя
              </span>
              <BaseInput
                placeholder="Карта/Номер счета/Телефон"
                leftIcon={<CardIcon className="text-primary dark:text-primary-dark" />}
                variant="secondary"
                className="w-full"
                value={filters.paymentRequisit}
                onChange={(e) =>
                  handleFilterChange('paymentRequisit', e.target.value.replace(/\D/g, ''))
                }
              />
            </div>
            <div>
              <span className="inline-block mb-2 text-text-secondary whitespace-nowrap">
                Сумма транзакции, фиат
              </span>
              <div className="flex items-center gap-x-2">
                <BaseInput
                  placeholder="От"
                  className="md:max-w-[100px]"
                  value={filters.amountFrom}
                  onChange={(e) =>
                    handleFilterChange('amountFrom', e.target.value.replace(/\D/g, ''))
                  }
                />
                <div className="w-4 h-[1px] bg-text-secondary"></div>
                <BaseInput
                  placeholder="До"
                  className="md:max-w-[100px]"
                  value={filters.amountTo}
                  onChange={(e) =>
                    handleFilterChange('amountTo', e.target.value.replace(/\D/g, ''))
                  }
                />
              </div>
            </div>
            <div>
              <span className="inline-block mb-2 text-text-secondary whitespace-nowrap">
                Статус транзакции
              </span>
              <ControlledSelect
                options={statusOptions}
                value={filters.status}
                onChange={(val) => handleFilterChange('status', val)}
                className="md:min-w-[200px]"
              />
            </div>
            <div>
              <span className="inline-block mb-2 text-text-secondary whitespace-normal">
                Дата создания транзакций
              </span>
              <div className="flex items-center gap-x-2 w-full">
                <DatePicker
                  variant="outline"
                  placeholder="Дата, от"
                  className="flex-row-reverse flex-1 justify-between !pr-2 !pl-3 !text-sm"
                  monthButton={false}
                  value={filters.startDate ? new Date(filters.startDate) : undefined}
                  onDateChange={(date) => {
                    const newStartDate = date.toISOString()

                    if (!filters.endDate || new Date(filters.endDate) >= date) {
                      handleFilterChange('startDate', newStartDate)
                      setError('')
                    } else {
                      setError('Дата начала не может быть позже даты окончания')
                    }
                  }}
                />
                <div className="w-4 h-[1px] bg-text-secondary"></div>
                <DatePicker
                  variant="outline"
                  placeholder="Дата, до"
                  className="flex-row-reverse flex-1 justify-between !pr-2 !pl-3 !text-sm"
                  monthButton={false}
                  value={filters.endDate ? new Date(filters.endDate) : undefined} // <-- тут
                  onDateChange={(date) => {
                    date.setHours(23, 59, 59, 999)
                    const newEndDate = date.toISOString()

                    if (!filters.startDate || date >= new Date(filters.startDate)) {
                      handleFilterChange('endDate', newEndDate)
                      setError('')
                    } else {
                      setError('Дата окончания не может быть раньше даты начала')
                    }
                  }}
                />
              </div>
            </div>
            <div>
              <span className="inline-block mb-2 text-text-secondary">Банк</span>
              <ControlledSelect
                options={bankOptions}
                variant="secondary"
                className="min-w-[130px] md:min-w-[200px]"
                value={filters.bank}
                onChange={(val) => handleFilterChange('bank', val)}
              />
            </div>
            <div>
              <span className="inline-block mb-2 text-text-secondary">Метод оплаты</span>
              <ControlledSelect
                options={paymentMethosOptions}
                variant="secondary"
                className="min-w-[130px] md:min-w-[200px]"
                value={filters.method}
                onChange={(val) => handleFilterChange('method', val)}
              />
            </div>
          </div>
          <div className="flex md:flex-row flex-col gap-2 md:gap-6">
            <BaseButton radius="lg" icon={<FilterIcon />} onClick={() => onSubmit(filters)}>
              Применить
            </BaseButton>
            <BaseButton
              onClick={handleReset}
              radius="lg"
              variant="ghost"
              icon={<CrossIcon className="text-primary" />}
              className="bg-light-bg dark:bg-dark-bg"
            >
              Сбросить фильтры
            </BaseButton>
          </div>
        </div>
      )}
    </div>
  )
}
